import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createWarranty, getDeviceWarranties, getWarranty, setWarrantyRemark, updateWarranty } from '../api/admin.js';
import type {
  AdminDeviceWarranty,
  DeviceWarrantyStatus,
  WarrantyClaim,
  WarrantyClaimState,
} from '@m8-app/contracts/rest/admin.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATES: WarrantyClaimState[] = ['open', 'in_review', 'approved', 'rejected', 'closed'];

const STATE_STYLES: Record<WarrantyClaimState, string> = {
  open:      'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  approved:  'bg-green-100 text-green-800',
  rejected:  'bg-red-100 text-red-800',
  closed:    'bg-gray-100 text-gray-700',
};

const STATUS_STYLES: Record<DeviceWarrantyStatus, string> = {
  active:  'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-700',
};

const DEVICE_STATUSES: DeviceWarrantyStatus[] = ['active', 'expired', 'unknown'];

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

export function Warranty() {
  const [tab, setTab] = useState<'devices' | 'claims'>('devices');

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Warranty</h2>
        <p className="text-sm text-gray-500">
          Per-device warranty coverage (same as the mobile app) and claims raised by admins.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {([['devices', 'Device Warranty'], ['claims', 'Claims']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'devices' ? <DevicesTab /> : <ClaimsTab />}
    </div>
  );
}

function DevicesTab() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<DeviceWarrantyStatus | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['warranty-devices', cursor, statusFilter, search],
    queryFn: () => getDeviceWarranties(cursor, statusFilter, search || undefined),
  });

  const devices = data?.devices ?? [];

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <label htmlFor="status-filter" className="text-sm text-gray-600">Status</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as DeviceWarrantyStatus | 'all'); setCursor(undefined); }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="all">All</option>
          {DEVICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); setCursor(undefined); }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="serial or name"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56"
          />
          <button type="submit" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Search
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Device</th>
              <th className="px-4 py-3 font-medium">Serial</th>
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Purchase</th>
              <th className="px-4 py-3 font-medium">Expiry</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
            )}
            {!isLoading && devices.map((d) => <DeviceWarrantyRow key={d.deviceId} device={d} />)}
            {!isLoading && devices.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No devices</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => setCursor(undefined)}
          disabled={!cursor}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
        >
          ← First page
        </button>
        <button
          onClick={() => data?.nextCursor && setCursor(data.nextCursor)}
          disabled={!data?.nextCursor}
          className="text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
        >
          Next page →
        </button>
      </div>
    </>
  );
}

function DeviceWarrantyRow({ device }: { device: AdminDeviceWarranty }) {
  return (
    <tr>
      <td className="px-4 py-3">
        <Link
          to={`/devices/${device.deviceId}`}
          className="text-indigo-600 hover:underline font-medium"
        >
          {device.name}
        </Link>
      </td>
      <td className="px-4 py-3 font-mono text-xs">{device.serialNumber}</td>
      <td className="px-4 py-3">{device.productType ?? '—'}</td>
      <td className="px-4 py-3 font-mono text-xs">{device.ownerMobile ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(device.purchaseDate)}</td>
      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(device.expiryDate)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[device.status]}`}>
          {device.status}
        </span>
      </td>
    </tr>
  );
}

function ClaimsTab() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [stateFilter, setStateFilter] = useState<WarrantyClaimState | 'all'>('all');
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['warranty', cursor, stateFilter],
    queryFn: () => getWarranty(cursor, stateFilter),
  });

  const filtered = data?.claims ?? [];

  return (
    <>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg font-medium"
        >
          + Raise Claim
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label htmlFor="state-filter" className="text-sm text-gray-600">State</label>
        <select
          id="state-filter"
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value as WarrantyClaimState | 'all'); setCursor(undefined); }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="all">All</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Device</th>
              <th className="px-4 py-3 font-medium">Description</th>
              <th className="px-4 py-3 font-medium">State</th>
              <th className="px-4 py-3 font-medium">Remark</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
            )}
            {!isLoading && filtered.map((c) => <ClaimRow key={c.id} claim={c} />)}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No claims</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => setCursor(undefined)}
          disabled={!cursor}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
        >
          ← First page
        </button>
        <button
          onClick={() => data?.nextCursor && setCursor(data.nextCursor)}
          disabled={!data?.nextCursor}
          className="text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-40"
        >
          Next page →
        </button>
      </div>

      {creating && <RaiseClaimModal onClose={() => setCreating(false)} />}
    </>
  );
}

function RaiseClaimModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [deviceId, setDeviceId] = useState('');
  const [description, setDescription] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const trimmedId = deviceId.trim();
  const idValid = UUID_RE.test(trimmedId);

  const { mutate, isPending } = useMutation({
    mutationFn: () => createWarranty({ deviceId: trimmedId, description: description.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warranty'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? null)
        : null;
      setErrorMsg(msg ?? 'Failed to raise claim. Please try again.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Raise Warranty Claim</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="device-id" className="block text-sm font-medium text-gray-700 mb-1">
              Device ID
            </label>
            <input
              id="device-id"
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="paste device UUID"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
            />
            {trimmedId && !idValid && (
              <p className="mt-1 text-xs text-red-600">Must be a valid UUID.</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Owner is resolved automatically from the device record.
            </p>
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="What's wrong with the device?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400 text-right">{description.length}/2000</p>
          </div>
          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMsg}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => mutate()}
            disabled={!idValid || isPending}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium"
          >
            {isPending ? 'Filing…' : 'File Claim'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClaimRow({ claim }: { claim: WarrantyClaim }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [remark, setRemark] = useState(claim.remark ?? '');

  const { mutate: changeState, isPending: stateBusy } = useMutation({
    mutationFn: (state: WarrantyClaimState) => updateWarranty(claim.id, { state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty'] }),
  });

  const { mutate: saveRemark, isPending: remarkBusy } = useMutation({
    mutationFn: () => setWarrantyRemark(claim.id, remark),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['warranty'] });
    },
  });

  return (
    <tr>
      <td className="px-4 py-3">
        <Link
          to={`/devices/${claim.deviceId}`}
          className="font-mono text-xs text-indigo-600 hover:underline"
        >
          {claim.deviceId.slice(0, 8)}…
        </Link>
      </td>
      <td className="px-4 py-3 max-w-md truncate">{claim.description || '—'}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLES[claim.state]}`}>
            {claim.state}
          </span>
          <select
            value={claim.state}
            disabled={stateBusy}
            onChange={(e) => changeState(e.target.value as WarrantyClaimState)}
            className="text-xs border border-gray-300 rounded px-1 py-0.5"
          >
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </td>
      <td className="px-4 py-3 max-w-[240px]">
        {editing ? (
          <div className="flex gap-1">
            <input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
            />
            <button
              onClick={() => saveRemark()}
              disabled={remarkBusy}
              className="text-xs px-2 py-1 bg-indigo-600 text-white rounded"
            >
              {remarkBusy ? '…' : 'Save'}
            </button>
            <button
              onClick={() => { setEditing(false); setRemark(claim.remark ?? ''); }}
              className="text-xs px-2 py-1 text-gray-500"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-left text-gray-600 hover:text-indigo-600 truncate w-full"
          >
            {claim.remark || <span className="italic text-gray-400">add remark</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-gray-400 text-xs">{claim.createdAt.slice(0, 10)}</td>
    </tr>
  );
}
